import { useEffect, useState } from "react";
import axios from 'axios';
import { toast } from 'react-toastify';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { format } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { useLoginStore } from "../zustand/StoreAuth/store";
import { useTranslation } from "react-i18next";
import ReactPixel from 'react-facebook-pixel';

type ProductInfo = {
  flat: string;
  price: string;
  bestOffer: string;
  expirationDate: string;
};

export default function usePaymentController() {
  const db = getFirestore();
  const { t } = useTranslation();

  const { userInfo } = useLoginStore();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [showProcessing, setShowProcessing] = useState(false);

  const userInfoString = localStorage.getItem('userInfo');
  const userInfoStore = JSON.parse(userInfoString as any);

  const customerInfo = {
    email: userInfo?.email || userInfoStore?.email || 'email_desconhecido',
    name: userInfo?.name || userInfoStore?.name || 'nome_desconhecido',
  };

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const successParam = searchParams.get("success");
      const failedParam = searchParams.get("canceled");
      const sessionId = localStorage.getItem('session_id');

      if (failedParam) {
        toast.error(t('toast_error_payment_cancelled'));
      }

      console.log(failedParam)

      if (successParam) {
        setLoading(true)
        try {
          const response = await axios.get(`https://us-central1-ab-gym.cloudfunctions.net/api/get-checkout-session?session_id=${sessionId}`);
          const paymentIntentId = response.data.payment_intent;
          localStorage.setItem('paymentIntentId', paymentIntentId);

          const fcmToken = "fIfSjxMIR7a8eIwnlJhCoq:APA91bE2Y8vIiztzCBWhztbaglz8d82D8vvsTWdhP7q6bC9HQzMiBexjnlOxbom2IBS5dAmQkwNSyRucfggqQCSLQ2fa-ZIfzIVepiIHCvUT0u-KyABH81KUmdGMC8aho4hYmTns0aFa";

          const interval = setInterval(async () => {
            setLoading(false)
            try {
              const statusResponse = await axios.post('https://us-central1-ab-gym.cloudfunctions.net/api/payment-status', {
                paymentIntentId,
                fcmToken
              });

              const data = statusResponse.data;
              console.log('Status do pagamento recebido:', data);
              setShowProcessing(true);

              if (data.status && (data.status === 'succeeded' || data.status === 'failed')) {
                handlePaymentStatus(data, paymentIntentId);
                clearInterval(interval);
              }
            } catch (error: any) {
              console.error('Erro ao consultar o status do pagamento:', error);

              if (axios.isAxiosError(error)) {
                console.error('Resposta do servidor:', error.response?.data);
              } else {
                console.error('Erro durante a requisição:', error.message);
              }
            }
          }, 10000);

          return () => clearInterval(interval);
        } catch (error) {
          console.error('Erro ao obter detalhes da sessão de checkout:', error);
        }
      }
    };

    checkPaymentStatus();
  }, [searchParams]);

  const handleCheckout = async (productInfo: ProductInfo) => {
    localStorage.setItem('productInfo', JSON.stringify(productInfo));

    try {
      const payload = {
        customerInfo,
        productInfo: {
          name: productInfo.flat,
          currency: t('curcurrency'),
          price: parseFloat(productInfo.price) * 100,
          description: productInfo.bestOffer,
          expirationDate: productInfo.expirationDate,
        },
      };

      const { data } = await axios.post<{ url: string, session_id: any }>('https://us-central1-ab-gym.cloudfunctions.net/api/create-checkout-session', payload);
      window.location.href = data.url;

      localStorage.setItem('session_id', data.session_id);
      ReactPixel.track('checkout', { action: 'finalização de compra' });
      toast.success(t('toast_success_checkout_started'));
    } catch (error) {
      console.error('Erro ao iniciar o checkout:', error);
      toast.error(t('toast_error_checkout_start'));
    }
  };

  const handlePaymentStatus = async (data: any, paymentIntentId: string) => {
    try {
      const userInfoString = localStorage.getItem('userInfo');
      const productInfoInfoString = localStorage.getItem('productInfo');

      if (!userInfoString || !productInfoInfoString) {
        toast.error(t('toast_error_user_not_found'));
        return;
      }

      const userInfoStore = JSON.parse(userInfoString);
      const productInfo = JSON.parse(productInfoInfoString);

      const dataAtual = new Date();
      const dataFormatada = format(dataAtual, 'dd/MM/yyyy');
      const formattedTime = `${String(dataAtual.getHours()).padStart(2, '0')}:${String(dataAtual.getMinutes()).padStart(2, '0')}`;

      const userData = {
        paymentIntentId,
        paymentStatus: data.status,
        successDateTime: formattedTime,
        successDate: dataFormatada,
        price: productInfo.price ?? '',
        namePremium: productInfo.flat,
        description: productInfo.bestOffer,
        gender: t('gender'),
        expirationDate: productInfo.expirationDate,
        name: userInfoStore?.name ?? '',
        email: userInfoStore?.email ?? ''
      };

      const userDocRef = doc(db, 'payment', userInfoStore.id);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        await setDoc(userDocRef, userData, { merge: true });
      } else {
        await setDoc(userDocRef, userData);
      }

      if (data.status === 'succeeded') {
        setShowSuccess(true);

        setTimeout(() => {
          ReactPixel.track('purchase', { action: 'compra feita' });
          window.location.href = '/downloadApp';
          localStorage.removeItem('userInfo');
          localStorage.removeItem('productInfo');
          localStorage.removeItem('paymentIntentId');
          localStorage.removeItem('session_id');
        }, 10000);
      } else if (data.status === 'failed') {
        ReactPixel.track('purchase error', { action: 'error de compra' });
        setShowFailure(true);
        window.location.href = '/promotion';
        toast.error(t('toast_error_try_again'));
      }
    } catch (error) {
      console.error('Erro ao processar status de pagamento:', error);
      toast.error(t('toast_error_payment_status'));
    }
  };

  return {
    handleCheckout,
    loading,
    showSuccess,
    showFailure,
    setShowSuccess,
    setShowFailure,
    showProcessing,
  };
}
